import React from 'react';
import { Link } from 'react-router-dom';
import { getIconUrl } from '../../services/asset';
import { checkEmptyObject, formatDate } from '../../services/utils';
import { EntityType, PageRoute } from '../../models/enums';
import FeatureFlags from '../../featureFlags';
import RatingCounter from '../RatingCounter';
import RatingButton from '../RatingButton';
import ReadMore from '../ReadMore';
import { IndexAvatar } from '../index';
import styles from './sellerInfoCard.scss';
import { SocialMediaLinks } from '../../containers/AdDetailsPage/SocialMediaLinks/SocialMediaLinks';
import FollowStoreButton from '../FollowStoreButton';
import { usePhoneReveal } from '../../hooks/usePhoneReveal';
import TurnstileChallenge from '../TurnstileChallenge';
import { formatLocationAddress } from '../../routes/Configuration/helpers';

interface SellerInfoCardProps {
  seller: any;
  onRateClick?: () => void;
  gaLabel?: string;
}

// Seller address from the location names the API returns with the seller
const constructAddress = (seller: any) => (seller ? formatLocationAddress(seller) : null);

const getRegistrationText = (registrationDate: string) => {
  if (!registrationDate) return null;
  return `Registriran od ${formatDate(registrationDate)}`;
};

const SellerInfoCard: React.FC<SellerInfoCardProps> = ({ seller, onRateClick, gaLabel }) => {
  if (!checkEmptyObject(seller)) {
    return null;
  }

  const address = constructAddress(seller);
  const membershipText = getRegistrationText(seller.registrationDate);
  const isLegalEntity = seller.legalEntity === EntityType.LegalEntity;
  const personType = isLegalEntity && FeatureFlags.SHOW_LEGAL_ENTITY
    ? "Pravna osoba"
    : "Fizička osoba";

  const hasWorkingHours = seller.workingHoursWorkingDay || seller.workingHoursSaturday || seller.workingHoursSunday;

  return (
    <div className={styles.card}>
      {/* Avatar */}
      <div className={styles.avatarWrapper}>
        <IndexAvatar
          url={seller.avatar}
          size={170}
          className={styles.avatar}
        />
      </div>

      {/* Username */}
      <div className={styles.usernameRow}>
        <Link
          to={`${PageRoute.user}/${encodeURIComponent(seller.username)}`}
          className={styles.usernameLink}
        >
          {seller.username}
        </Link>
      </div>

      {/* Rating counter */}
      <Link
        to={`${PageRoute.user}/${encodeURIComponent(seller.username)}${PageRoute.userRatings}`}
        className={styles.ratingWrapper}
      >
        <RatingCounter user={seller} />
      </Link>

      {/* All seller ads link */}
      <Link
        to={`${PageRoute.user}/${encodeURIComponent(seller.username)}`}
        className={styles.allAdsLink}
      >
        Svi oglasi oglašivača
      </Link>

      {/* Follow store button — only for sellers marked as a store (have dominant categories) */}
      {Array.isArray(seller.dominantCategories) && seller.dominantCategories.length > 0 && (
        <div className={styles.followWrapper}>
          <FollowStoreButton storeId={seller.id || seller.userId} />
        </div>
      )}

      {/* Divider after rating */}
      <div className={styles.divider} />

      {/* Description for business entities */}
      {isLegalEntity && seller.description && (
        <div className={styles.descriptionSection}>
          <ReadMore text={seller.description} />
        </div>
      )}

      {/* Info section - two columns for business, single column for private */}
      {isLegalEntity && hasWorkingHours ? (
        <div className={styles.twoColumnSection}>
          <div className={styles.column}>
            <div className={styles.columnTitle}>Osnovne informacije</div>
            {address && (
              <div className={styles.infoItem}>{address}</div>
            )}
            {membershipText && (
              <div className={styles.infoItem}>{membershipText}</div>
            )}
            <div className={styles.infoItem}>{personType}</div>
          </div>
          <div className={styles.column}>
            <div className={styles.columnTitle}>Radno vrijeme</div>
            {seller.workingHoursWorkingDay && (
              <div className={styles.infoItem}>Radni dan: {seller.workingHoursWorkingDay}</div>
            )}
            {seller.workingHoursSaturday && (
              <div className={styles.infoItem}>Subota: {seller.workingHoursSaturday}</div>
            )}
            {seller.workingHoursSunday && (
              <div className={styles.infoItem}>Nedjelja: {seller.workingHoursSunday}</div>
            )}
          </div>
        </div>
      ) : (
        <div className={styles.infoSection}>
          {address && (
            <div className={styles.infoItem}>{address}</div>
          )}
          {membershipText && (
            <div className={styles.infoItem}>{membershipText}</div>
          )}
          <div className={styles.infoItem}>{personType}</div>
        </div>
      )}

      {/* Phone section */}
      {(seller.isVerified || seller.hasPhones) && (
        <SellerPhoneSection seller={seller} />
      )}

      {/* Response rate */}
      {seller.responseRate && (
        <div className={styles.responseRate}>
          Odgovara na {seller.responseRate}% poruka u roku od {seller.responseTime || '5 sati'}
        </div>
      )}

      {/* Rate button */}
      <div className={styles.buttonWrapper}>
        <RatingButton user={seller} gaLabel={gaLabel} />
      </div>
    </div>
  );
};

interface SellerPhoneSectionProps {
  seller: any;
}

// Reveals seller numbers on click through the rate-limited /phone/reveal/user
// endpoint. Rendered as a sibling so the hook doesn't run for every
// SellerInfoCard on the page — only when the phone section actually mounts.
const SellerPhoneSection: React.FC<SellerPhoneSectionProps> = ({ seller }) => {
  const sellerId = seller.id || seller.userId;
  const { state, phones, hardLimitMessage, reveal } = usePhoneReveal({ kind: "user", id: sellerId });

  return (
    <div className={styles.phoneSection}>
      <SocialMediaLinks seller={seller} minified />
      {seller.isVerified && (
        <div className={styles.verifiedPhone}>
          <img
            src={getIconUrl('verified.svg')}
            alt="Verified"
            className={styles.verifiedBadgeSmall}
          />
          <span>Broj je verificiran</span>
        </div>
      )}

      {seller.hasPhones && state === "idle" && (
        <button
          type="button"
          onClick={() => reveal()}
          className={styles.phoneRevealButton}
        >
          Prikaži brojeve telefona
        </button>
      )}

      {state === "loading" && (
        <div className={styles.phoneRevealLoading}>Učitavam…</div>
      )}

      {state === "captchaRequired" && (
        <TurnstileChallenge onSuccess={(token) => reveal(token)} />
      )}

      {state === "hardLimit" && (
        <div className={styles.phoneRevealLimit}>
          {hardLimitMessage || "Prekoračili ste dnevni limit pregleda telefonskih brojeva. Pokušajte ponovno kasnije."}
        </div>
      )}

      {state === "revealed" && phones.map((phone: any, index: number) => (
        <div key={index} className={styles.phoneNumber}>
          <a href={`tel:${phone.number}`} className={styles.phoneNumberLink}>{phone.number}</a>
          {phone.name && <span className={styles.phoneName}>{phone.name}</span>}
        </div>
      ))}
    </div>
  );
};

export default SellerInfoCard;



// WEBPACK FOOTER //
// ./src/components/SellerInfoCard/index.tsx